import { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from '@/lib/toast';
import type { UserItem } from '@/types/userItem';
import { hasOnlyStockPhoto, STOCK_PHOTO_BLOCK_TITLE, STOCK_PHOTO_BLOCK_DESC } from '@/lib/listingEligibility';
import { patchItemInCaches, restoreItemCaches } from '@/lib/optimisticItems';


interface ProfileDefaults {
  city: string | null;
  stateCode: string | null;
  willShip: boolean;
  acceptsTrades: boolean;
  loaded: boolean;
}

/**
 * Shared quick-list mutation used by the row-level popover, inline card price,
 * and any future one-shot listing surface.
 *
 * Pulls seller defaults (location, will_ship, accepts_trades) from `profiles`
 * so callers don't have to re-implement the boilerplate that lives in
 * QuickListDialog / QuickPriceEditDialog.
 */
export function useQuickListItem() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [profile, setProfile] = useState<ProfileDefaults>({
    city: null,
    stateCode: null,
    willShip: false,
    acceptsTrades: false,
    loaded: false,
  });

  useEffect(() => {
    let cancelled = false;
    if (!user) {
      setProfile((p) => ({ ...p, loaded: true }));
      return;
    }
    (async () => {
      const { data } = await supabase
        .from('profiles')
        .select('city, state_code, will_ship_items, accepts_trades')
        .eq('user_id', user.id)
        .maybeSingle();
      if (cancelled) return;
      setProfile({
        city: data?.city ?? null,
        stateCode: data?.state_code ?? null,
        willShip: data?.will_ship_items ?? false,
        acceptsTrades: data?.accepts_trades ?? false,
        loaded: true,
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const hasLocation = !!(profile.city && profile.stateCode);

  const listItem = useCallback(
    async (
      item: UserItem,
      opts: { priceCents: number | null; acceptsOffers: boolean; title?: string },
    ): Promise<boolean> => {
      if (hasOnlyStockPhoto(item)) {
        toast.error(STOCK_PHOTO_BLOCK_TITLE, { description: STOCK_PHOTO_BLOCK_DESC });
        return false;
      }
      if (!opts.priceCents && !opts.acceptsOffers) {
        toast.error('Enter a price or enable "Open to offers"');
        return false;
      }

      // Guard against firing "Location required" during the profile-fetch race:
      // if state isn't hydrated yet (or looks empty), read the profile fresh
      // before deciding.
      let city = profile.city;
      let stateCode = profile.stateCode;
      let willShip = profile.willShip;
      let acceptsTrades = profile.acceptsTrades;
      if ((!profile.loaded || !city || !stateCode) && user) {
        const { data } = await supabase
          .from('profiles')
          .select('city, state_code, will_ship_items, accepts_trades')
          .eq('user_id', user.id)
          .maybeSingle();
        city = data?.city ?? null;
        stateCode = data?.state_code ?? null;
        willShip = data?.will_ship_items ?? false;
        acceptsTrades = data?.accepts_trades ?? false;
        setProfile({ city, stateCode, willShip, acceptsTrades, loaded: true });
      }
      if (!city || !stateCode) {
        toast.error('Set your location in Profile to list items');
        return false;
      }

      const patch: Record<string, unknown> = {
        visibility: 'listed',
        is_listed: true,
        asking_price_cents: opts.priceCents,
        is_negotiable: opts.acceptsOffers,
        accepts_trades: acceptsTrades,
        ships_anywhere: willShip,
        city,
        state_code: stateCode,
      };
      if (opts.title !== undefined && opts.title.trim().length > 0) {
        patch.title = opts.title.trim();
      }
      // Optimistic: flip the row to "listed" everywhere before the round-trip.
      const snapshot = patchItemInCaches(queryClient, item.id, patch);

      const { error } = await supabase
        .from('user_items')
        .update(patch as never)
        .eq('id', item.id);

      if (error) {
        restoreItemCaches(queryClient, snapshot);
        console.error('quick-list failed', error);
        toast.error('Failed to list item');
        return false;
      }


      queryClient.invalidateQueries({ queryKey: ['my-items'] });
      queryClient.invalidateQueries({ queryKey: ['user-item', item.id] });
      queryClient.invalidateQueries({ queryKey: ['marketplace-items'] });
      toast.success('Listed');
      return true;
    },
    [profile, user, queryClient],
  );

  return { listItem, hasLocation, profileLoaded: profile.loaded };
}
